import {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  type ReactNode,
} from "react";
import { graphqlClient } from "@/lib/graphql";
import { gql } from "graphql-request";
import type { User } from "@/types";

interface AuthContextType {
  user: User | null;
  loading: boolean;
  login: (email: string, password: string) => Promise<string[]>;
  register: (
    email: string,
    password: string,
    displayName: string
  ) => Promise<{ errors: string[]; message?: string }>;
  logout: () => void;
  setToken: (token: string) => Promise<void>;
  updateUser: (user: User) => void;
}

const AuthContext = createContext<AuthContextType | null>(null);

const ME_QUERY = gql`
  query Me {
    me {
      id
      email
      displayName
      bio
      avatarUrl
      rating
      ratingsCount
      createdAt
      admin
      activeListingsCount
      latitude
      longitude
      city
      address
      defaultDelivery
      defaultPickup
      defaultPickupRadius
      emailNotificationFrequency
      browserNotificationsEnabled
      quietHoursEnabled
      quietHoursStart
      quietHoursEnd
    }
  }
`;

const SIGN_IN_MUTATION = gql`
  mutation SignIn($email: String!, $password: String!) {
    signIn(email: $email, password: $password) {
      token
      user {
        id
        email
        displayName
        bio
        avatarUrl
        rating
        ratingsCount
        admin
        createdAt
        activeListingsCount
        latitude
        longitude
        city
        address
        defaultDelivery
        defaultPickup
        defaultPickupRadius
        emailNotificationFrequency
        browserNotificationsEnabled
        quietHoursEnabled
        quietHoursStart
        quietHoursEnd
      }
      errors
    }
  }
`;

const SIGN_UP_MUTATION = gql`
  mutation SignUp(
    $email: String!
    $password: String!
    $displayName: String!
  ) {
    signUp(email: $email, password: $password, displayName: $displayName) {
      message
      errors
    }
  }
`;

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  const fetchUser = useCallback(async () => {
    const token = localStorage.getItem("auth_token");
    if (!token) {
      setLoading(false);
      return;
    }

    try {
      const data = await graphqlClient.request<{ me: User | null }>(ME_QUERY);
      setUser(data.me);
    } catch (err) {
      console.error("Auth fetch failed:", err);
      localStorage.removeItem("auth_token");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchUser();
  }, [fetchUser]);

  const login = async (email: string, password: string): Promise<string[]> => {
    const data = await graphqlClient.request<{
      signIn: { token: string | null; user: User | null; errors: string[] };
    }>(SIGN_IN_MUTATION, { email, password });

    if (data.signIn.token && data.signIn.user) {
      localStorage.setItem("auth_token", data.signIn.token);
      setUser(data.signIn.user);
      return [];
    }
    return data.signIn.errors;
  };

  const register = async (
    email: string,
    password: string,
    displayName: string
  ): Promise<{ errors: string[]; message?: string }> => {
    const data = await graphqlClient.request<{
      signUp: { message: string | null; errors: string[] };
    }>(SIGN_UP_MUTATION, { email, password, displayName });

    if (data.signUp.errors.length === 0) {
      return { errors: [], message: data.signUp.message || undefined };
    }
    return { errors: data.signUp.errors };
  };

  const logout = () => {
    localStorage.removeItem("auth_token");
    setUser(null);
  };

  const updateUser = useCallback((updatedUser: User) => {
    setUser(updatedUser);
  }, []);

  const setToken = useCallback(async (token: string) => {
    localStorage.setItem("auth_token", token);
    await fetchUser();
  }, [fetchUser]);

  return (
    <AuthContext.Provider
      value={{ user, loading, login, register, logout, setToken, updateUser }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
}
